import { motion, useInView } from "framer-motion";
import { useRef, useState } from "react";
import { GraduationCap, Clock, Layers, Award, ArrowRight, Check } from "lucide-react";
import { useLanguage } from "../../context/LanguageContext";
import InterestModal from "./InterestModal";

const translations = {
  overline: { es: "Programa insignia · Avalado por PCMA", en: "Flagship program · Endorsed by PCMA" },
  title: {
    es: "Diplomado en Turismo de Reuniones",
    en: "Diploma in Meetings Tourism",
  },
  subtitle: {
    es: "Un recorrido formativo integral de 96 horas para profesionalizar equipos completos con el marco educativo de PCMA Institute.",
    en: "A comprehensive 96-hour training journey to professionalize entire teams with PCMA Institute's education framework.",
  },
  modulesLabel: { es: "Contenido del diplomado", en: "Diploma content" },
  modules: {
    es: [
      { number: "01", title: "Fundamentos de la Industria de Reuniones", description: "Estructura, actores, tipos de eventos, impacto económico y tendencias del sector." },
      { number: "02", title: "Operación de Eventos", description: "Logística, estándares internacionales, gestión operativa y experiencia del participante." },
      { number: "03", title: "Proceso Comercial de la Industria de Reuniones", description: "Bidding, estrategia comercial, ventas consultivas y desarrollo de propuestas ganadoras." },
      { number: "04", title: "Marketing para Destinos de Reuniones", description: "Branding, posicionamiento, segmentación y estrategias para atraer eventos internacionales." },
    ],
    en: [
      { number: "01", title: "Foundations of the Meetings Industry", description: "Structure, stakeholders, event types, economic impact, and industry trends." },
      { number: "02", title: "Event Operations", description: "Logistics, international standards, operational management, and participant experience." },
      { number: "03", title: "Commercial Process in the Meetings Industry", description: "Bidding, commercial strategy, consultative selling, and winning proposal development." },
      { number: "04", title: "Marketing for Meetings Destinations", description: "Branding, positioning, segmentation, and strategies to attract international events." },
    ],
  },
  specs: {
    duration: { es: "96 horas totales · 24 horas por módulo", en: "96 total hours · 24 hours per module" },
    modality: { es: "Presencial y/o en línea", en: "In-person and/or online" },
    format: { es: "4 módulos", en: "4 modules" },
    endorsement: { es: "Avalado por PCMA", en: "Endorsed by PCMA" },
  },
  investmentLabel: { es: "Inversión de referencia", en: "Reference investment" },
  investmentValue: { es: "USD 36,000", en: "USD 36,000" },
  investmentNote: {
    es: "Por el diplomado completo. Ajustable según formato, calendario y personalización para tu organización.",
    en: "For the complete diploma. Adjustable based on format, schedule, and customization for your organization.",
  },
  cta: { es: "Solicitar propuesta del diplomado", en: "Request diploma proposal" },
};

const DiplomadoSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  const { t } = useLanguage();
  const [interestOpen, setInterestOpen] = useState(false);

  const modules = t(translations.modules);

  return (
    <section
      id="diplomado"
      data-testid="diplomado-section"
      ref={ref}
      className="relative py-24 md:py-32 bg-[#0B1F3B] overflow-hidden"
    >
      {/* Decorative accents */}
      <div className="absolute top-0 right-0 w-[600px] h-[400px] bg-[#F59E0B]/10 rounded-full blur-[150px] pointer-events-none" />
      <div className="absolute bottom-0 left-0 w-[500px] h-[400px] bg-[#2ED3C6]/10 rounded-full blur-[130px] pointer-events-none" />

      <div className="relative max-w-6xl mx-auto px-6 md:px-12">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.7 }}
          className="text-center mb-14 md:mb-20 max-w-3xl mx-auto"
        >
          <span className="inline-flex items-center gap-2 text-[#F59E0B] text-xs md:text-sm uppercase tracking-[0.2em] font-bold font-body mb-4">
            <GraduationCap className="w-4 h-4" />
            {t(translations.overline)}
          </span>
          <h2
            data-testid="diplomado-title"
            className="font-display text-3xl md:text-4xl lg:text-5xl text-white font-medium tracking-tight mb-5"
          >
            {t(translations.title)}
          </h2>
          <p className="font-body text-white/70 text-base md:text-lg leading-relaxed">
            {t(translations.subtitle)}
          </p>
        </motion.div>

        {/* Specs strip */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-12"
        >
          {[
            { icon: Clock, label: t(translations.specs.duration) },
            { icon: Layers, label: t(translations.specs.format) },
            { icon: GraduationCap, label: t(translations.specs.modality) },
            { icon: Award, label: t(translations.specs.endorsement) },
          ].map((spec, i) => {
            const Icon = spec.icon;
            return (
              <div
                key={i}
                className="flex items-start gap-3 p-4 bg-white/5 border border-white/10 rounded-xl"
              >
                <div className="w-10 h-10 rounded-lg bg-[#2ED3C6]/20 flex items-center justify-center shrink-0">
                  <Icon className="w-4 h-4 text-[#2ED3C6]" />
                </div>
                <span className="font-body text-white/85 text-sm leading-snug pt-1.5">
                  {spec.label}
                </span>
              </div>
            );
          })}
        </motion.div>

        {/* Modules grid */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.7, delay: 0.35 }}
          className="mb-12"
        >
          <div className="inline-flex items-center gap-2 mb-6">
            <span className="w-8 h-[2px] bg-[#F59E0B]" />
            <span className="font-body text-white/70 text-xs uppercase tracking-[0.2em] font-bold">
              {t(translations.modulesLabel)}
            </span>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-5">
            {modules.map((module, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                animate={isInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.5, delay: 0.4 + i * 0.1 }}
                data-testid={`diplomado-module-${i + 1}`}
                className="group p-6 bg-white/5 border border-white/10 rounded-2xl hover:bg-white/10 hover:border-[#F59E0B]/40 transition-all duration-300"
              >
                <div className="flex items-start gap-4">
                  <span className="font-display text-3xl text-[#F59E0B] font-bold shrink-0">
                    {module.number}
                  </span>
                  <div>
                    <h3 className="font-display text-lg md:text-xl text-white font-semibold mb-2 leading-tight">
                      {module.title}
                    </h3>
                    <p className="font-body text-white/65 text-sm leading-relaxed">
                      {module.description}
                    </p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Investment CTA block */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.7, delay: 0.6 }}
          className="bg-gradient-to-br from-[#F59E0B]/15 to-[#F59E0B]/5 border border-[#F59E0B]/30 rounded-3xl p-8 md:p-10"
        >
          <div className="grid grid-cols-1 md:grid-cols-5 gap-6 items-center">
            <div className="md:col-span-3">
              <div className="font-body text-[#F59E0B] text-xs uppercase tracking-[0.2em] font-bold mb-2">
                {t(translations.investmentLabel)}
              </div>
              <div className="font-display text-4xl md:text-5xl text-white font-bold tracking-tight mb-3">
                {t(translations.investmentValue)}
              </div>
              <p className="font-body text-white/70 text-sm leading-relaxed">
                {t(translations.investmentNote)}
              </p>
            </div>
            <div className="md:col-span-2 flex md:justify-end">
              <button
                type="button"
                onClick={() => setInterestOpen(true)}
                data-testid="diplomado-cta"
                className="inline-flex items-center gap-2 bg-[#F59E0B] hover:bg-[#D97706] text-[#0B1F3B] font-display font-bold py-4 px-6 rounded-full transition-all hover:-translate-y-0.5 hover:shadow-xl hover:shadow-[#F59E0B]/30 group"
              >
                <Check className="w-4 h-4" />
                {t(translations.cta)}
                <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
              </button>
            </div>
          </div>
        </motion.div>
      </div>

      <InterestModal
        open={interestOpen}
        onClose={() => setInterestOpen(false)}
        course={{ id: 999, name: "Diplomado en Turismo de Reuniones" }}
      />
    </section>
  );
};

export default DiplomadoSection;
